<template>
    <div>
        <Form 
            class="deliverModalForm"
            style="padding:20px"
            :form="deliverForm" 
            @handleSelectChange="handleDeliverSelectChange"
            @handleInput="handleDeliverInput"
        ></Form>
        <a-divider orientation="center">
            <div style="font-weight:bold">{{ $t('order.deliverDetailTitle') }}</div>
        </a-divider>
        <a-table 
            class="deliverTable" 
            :columns="deliverTable.columns" 
            :data="deliverTable.datas" 
            :loading="deliverTable.configs.loading" 
            :pagination="false"
        >
            <template #warehouse="{record}">
                    <a-select 
                        v-model="record.warehouse" 
                        :style="{width:'270px'}" 
                        placeholder="Please select ..."
                    >
                        <a-option
                            v-for="(item,index) in record.wmsOptions"
                            :key="index"
                            :value="item.value"
                            :disabled="item.disabled"
                        >{{ item.label }}</a-option>
                    </a-select>
            </template>
            <template #modelModelYear="{record}">
                    <div v-if="record.model == '-' && record.model_year == '-'" style="font-size:12px">-</div>
                    <div v-else style="font-size:12px">{{ record.model }}，{{record.model_year}}</div>
            </template>
        </a-table>
    </div>
</template>

<script setup>
    import { nextTick, onMounted, reactive, ref, watch, toRefs, h } from 'vue';
    import { PrepaidOrderApi } from '@/api/PrepaidOrderApi.js';
    import { OrderApi } from '@/api/OrderApi.js'; 
    import { Message, Modal } from '@arco-design/web-vue';
    import { useRouter } from 'vue-router';
    import Table from '@/components/Table.vue';
    import Form from '@/components/Form.vue';
    import { 
        PrepaidOrderQuery,
        DeliverForm
    } from './queryConfig/PrepaidOrderQuery.js';
    
    import { dateToTimestamp, timestampToDate, formatterToPoint, formatterToComma, getNowDate, getWeek } from '@/utils/index.js';
    import { useI18n } from 'vue-i18n';
    import { useLocale } from '@/store/index.js';
    import { multiply, add, subtract, divide } from 'accurate-core';

    const router = useRouter()
    const localeStore = useLocale()
    const { t } = useI18n()

    const props = defineProps({
        row:{
            type:Object,
            default:()=>{}
        }
    })


    const courierId = ref('')
    const deliverForm = reactive(DeliverForm)
    const warehouseOptions = ref([])
    const bomAvailableWarehouses = ref([])
    const deliverTable = ref({
        datas:[],
        columns:[
            {
                // title: '#',
                title: '#',
                dataIndex: 'serial',
                align:'center',
                ellipsis:true,
                tooltip:true,
                width:60,
                show:true,
            },
            {
                // title: '订单编号',
                title: t('order.orderName'),
                dataIndex: 'order_name',
                align:'center',
                ellipsis:true,
                tooltip:true,
                width:160,
                show:true,
            },
            {
                // title: 'SKU',
                title: t('order.sku'),
                dataIndex: 'sku',
                align:'center',
                ellipsis:true,
                tooltip:true,
                show:true,
                width:160,
            },
            {
                // title: '物料编码',
                title: t('order.inventoryCode'),
                dataIndex: 'inventory_code',
                align:'center',
                ellipsis:true,
                tooltip:true,
                show:true,
                width:140,
            },
            {
                // title: 'ERP行号',
                title:t('order.erpLineNo'),
                dataIndex: 'erp_line_no',
                align: 'center',
                ellipsis:true,
                tooltip:true,
                show:true,
                width:110,
            },
            {
                // title: '产品名称',
                title: t('order.productName'),
                dataIndex: 'product_name',
                align:'center',
                ellipsis:true,
                tooltip:true,
                show:true,
                width:160,
            },
            {
                // title: '车型/年份',
                title:t('order.modelModelYear'),
                dataIndex: 'model_model_year',
                align:'center',
                ellipsis:true,
                tooltip:true,
                show:true,
                width:200,
                slotName:'modelModelYear',
            },
            {
                // title: '下单数量',
                title: t('order.quantity'),
                dataIndex: 'orderQuantity',
                align:'center',
                ellipsis:true,
                tooltip:true,
                show:true,
                width:100,
            },
            {
                // title: '发货仓库',
                title: t('order.wms'),
                dataIndex: 'warehouse',
                align:'center',
                width:300,
                show:true,
                slotName:'warehouse',
                fixed:'right',
            },
        ],
        configs:{
            loading:false,
        }
    })


    // 物流单号
    const handleDeliverInput = (item,val)=>{
        if(item.key == 'courier_no'){
            deliverForm.formData[item.key] = val.replace(/[^a-zA-Z0-9]/g,'')
        }
    }


    // 获取库存仓库下拉配置
    const getWarehouseConfig = async()=>{
        let param = {
            dict_code: "config",
            dict_sub_code:'bom_available_warehouse',
        }
        let res = await OrderApi.getInstance().getWarehouseConfig(param)
        if(res.code == 0){
            bomAvailableWarehouses.value = res.data.map(item => item.dict_sub_value)
        }else{
            Message.error(res.msg)
        }
        
    }

    // 获取订单行
    const getPrepaidOrderLine = async()=>{
        let param = {
            prepaid_order_id:props.row.id
        }
        let res = await PrepaidOrderApi.getInstance().getPrepaidOrderLine(param)
        if(res.code == 0){
            deliverTable.value.datas = []

            // 整车可选仓库
            // let warehouse_arr = ['HFFG05','HF_OP04','HF_OP02','HFRM01','HFRM03','HFRM04']
            let wmsOptions = []
            warehouseOptions.value.forEach(item=>{
                wmsOptions.push({
                    ...item,
                    // disabled: warehouse_arr.includes(item.value),
                })
            })
            console.log(wmsOptions, 'wmsOptions')

            res.data.length > 0 && res.data.forEach((i,index)=>{
                // 配件可选仓库
                let wmsOptions2 = []
                warehouseOptions.value.forEach(m=>{
                    wmsOptions2.push({
                        ...m,
                        disabled: !bomAvailableWarehouses.value.includes(m.value),
                        // disabled: (i.product_code != '627.501' && m.value != 'HFRM03' && m.value != 'HFRM05'),
                    })
                })
                console.log(wmsOptions2, 'wmsOptions2')

                deliverTable.value.datas.push({
                    ...i,
                    serial:index + 1,
                    product_name: !i.product_map? '-' : !i.product_map.english_name? '-' : i.product_map.english_name,
                    orderQuantity:i.quantity,
                    model: !i.product_map? '-' : !i.product_map.model? '-' : i.product_map.model,
                    model_year: !i.product_map? '-' : !i.product_map.model_year? '-' : i.product_map.model_year,
                    warehouse: i.product_code == '627.501'? 'HFFG01' : bomAvailableWarehouses.value[1],
                    wmsOptions: i.product_code == '627.501'? wmsOptions : wmsOptions2,
                })
            })

            console.log( deliverTable.value.datas,' props.row')
            deliverForm.formData.transaction_date = res.data[0].handle_timestamp * 1000 
        }else{
            Message.error(res.msg)
        }
    }


    // 发货
    const deliver = async()=>{
        if(!!deliverForm.formData.courier_type && (deliverForm.formData.courier_no !== '' || deliverForm.formData.courier_url !== '')){
            await getCourierId()
        }
        
        // console.log(deliverTable.value.datas,'deliverTable.value.datas')
        let param = deliverTable.value.datas.map(i=>{
            return {
                courier_id:courierId.value,
                org_id:203,
                status:0,
                order_id: i.order_id,
                erp_no: props.row.erp_no,
                order_line_id: i.order_line_id,
                erp_line_no:i.erp_line_no,
                inventory_code:i.inventory_code,
                quantity: i.quantity,
                courier_no: deliverForm.formData.courier_no,
                courier_url:  deliverForm.formData.courier_url,
                courier_type: deliverForm.formData.courier_type,
                transaction_date: !deliverForm.formData.transaction_date? '' : dateToTimestamp(deliverForm.formData.transaction_date),
                warehouse:i.warehouse,
            }
        })

        console.log(param,'bbbnnnn')
        let res = await PrepaidOrderApi.getInstance().orderDelivery(param, props.row.order_id, props.row.id)
        if(res.code == 0){
            Message.success(t('base.operateSuccess'))
        }else{
            Message.error(res.msg)
        }
    }

     // 获取仓库
     const getWarehouse = async ()=>{
        let res = await OrderApi.getInstance().getWarehouse()
        if(res.code == 0){
            res.data.length > 0 && res.data.forEach(i=>{
                if(i.subinventory_name !== 'HF_OP02' && i.subinventory_name !== 'HFRM01'){
                    warehouseOptions.value.push({
                        label: `${i.description} (${i.subinventory_name})`,
                        value: i.subinventory_name,
                    })
                }
            })
        }else{
            Message.error(res.msg)
        }
    }

    // 获取物流公司
    const getCourierType = async ()=>{
        let res = await OrderApi.getInstance().getCourierType({dict_code: "courier"})
        if(res.code == 0){
            let item = deliverForm.formField.find(i=> i.key == 'courier_type')
            item.options = []
            res.data.length > 0 && res.data.forEach(i=>{
                item.options.push({
                    label: i.dict_sub_name,
                    value: i.dict_sub_value,
                })
            })
        }else{
            Message.error(res.msg)
        }
    }

    // 获取物流id
    const getCourierId = async ()=>{
        let param = {
            courier_type: deliverForm.formData.courier_type,
            courier_no: deliverForm.formData.courier_no,
            courier_url: deliverForm.formData.courier_url,
            courier_status:0,
        }
        let res = await OrderApi.getInstance().getCourierId(param)
        if(res.code == 0){
            courierId.value = res.data.id
        }else{
            Message.error(res.msg)
        }
    }

    defineExpose({
        deliver
    })


    onMounted(async()=>{
        await getWarehouse()
        await getWarehouseConfig()
        getPrepaidOrderLine()
        
        getCourierType()

        deliverForm.formField.forEach(i=>{
            deliverForm.formData[i.key] = ''
        })
    })

</script>

<style lang="less" scoped>
.deliverTable{
        /deep/ .arco-table-th{
            background-color: #333 !important;
            color: #fff;
            border-bottom: 1px solid #eee;
            padding: 0;
            font-weight: 300;
            font-size:12px;
        }
}
</style>